<template>
    <v-menu
        v-bind="positionProps"
        v-model="showNotes"
        content-class="notes-wrapper"
        :close-on-content-click="false"
        offset-y
    >
        <template v-slot:activator="{ props }">
            <v-btn
                v-bind="props"
                :disabled="disabled"
                :icon="`${hasNotes ? 'custom:book-notification' : 'custom:book'}`"
                density="comfortable"
                elevation="0"
                class="notes-icon"
                :class="{ 'notes-icon--active': hasNotes }"
            />
        </template>

        <div class="notes">
            <div class="notes__header">
                <h3>{{ toSentenceCase($t('notes.title')) }}</h3>
                <v-btn
                    icon="custom:cancel"
                    density="comfortable"
                    size="x-small"
                    elevation="0"
                    @click="close"
                />
            </div>
            <div v-if="!showConfirmDelete">
                <div v-for="note in notes" :key="note._id" class="notes__container">
                    <v-avatar class="notes__author" color="primary" size="26">
                        <span class="white--text headline">{{ getInitials(note) }}</span>
                    </v-avatar>
                    <div class="notes__content">
                        <div class="notes__date-container">
                            <p class="notes__date">{{ dateTime(note.lastUpdatedOn) }}</p>
                            <v-menu v-if="!noteActionsDisabled(note)" close-on-click>
                                <template v-slot:activator="{ props }">
                                    <v-icon v-bind="props" color="black"
                                        >mdi-dots-horizontal</v-icon
                                    >
                                </template>
                                <v-list density="compact" @click.stop>
                                    <v-list-item @click.stop="editNote(note)">
                                        {{ toSentenceCase($t('actions.edit')) }}
                                    </v-list-item>
                                    <v-list-item @click.stop="deleteNote(note._id)">
                                        {{ toSentenceCase($t('actions.delete')) }}
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </div>
                        <div class="notes__text">
                            <div v-if="noteToEdit === note._id" class="notes__input">
                                <v-textarea
                                    v-model="editText"
                                    :placeholder="toSentenceCase($t('notes.editNotePlaceholder'))"
                                    hide-details
                                    no-resize
                                    solo
                                    autocomplete
                                    flat
                                    rows="0"
                                />
                                <div class="notes__button float-right">
                                    <v-btn secondary small depressed height="18" @click="clear">{{
                                        toSentenceCase($t('actions.cancel'))
                                    }}</v-btn>
                                    <v-btn
                                        primary
                                        small
                                        depressed
                                        height="18"
                                        class="ml-1"
                                        :disabled="isSaveButtonDisabled(editText)"
                                        @click="save(note)"
                                        >{{ toSentenceCase($t('actions.save')) }}</v-btn
                                    >
                                </div>
                            </div>
                            <p v-else>{{ note.text }}</p>
                        </div>
                    </div>
                </div>
                <div class="notes__input-container">
                    <div class="notes__input">
                        <v-textarea
                            v-model="text"
                            :placeholder="toSentenceCase($t('notes.addNewNotePlaceholder'))"
                            hide-details
                            no-resize
                            solo
                            autocomplete
                            flat
                            rows="0"
                        />
                    </div>
                    <div class="notes__button">
                        <v-btn
                            primary
                            small
                            depressed
                            height="18"
                            :disabled="isSaveButtonDisabled(text)"
                            @click="post"
                        >
                            {{ toSentenceCase($t('notes.postNoteButton')) }}
                        </v-btn>
                    </div>
                </div>
            </div>
            <confirm-action
                v-else
                :action-text="$t('actions.delete')"
                :question-text="$t('notes.confirmDeleteQuestion')"
                :warning-text="$t('notes.confirmDeleteWarning')"
                @cancel="clear"
                @confirm="confirmDelete"
            />
        </div>
    </v-menu>
</template>

<script>
import { isEmpty } from 'lodash';
import { mapState } from 'vuex';

export default {
    props: {
        notes: Array,
        disabled: {
            type: Boolean,
            default: false,
        },
        openLeft: {
            type: Boolean,
            default: false,
        },
        openRight: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['post', 'save', 'delete'],

    data: () => ({
        overrideReadOnly: true,
        showNotes: false,
        text: null,
        editText: null,
        noteToEdit: null,
        noteToDelete: null,
        showConfirmDelete: false,
    }),

    computed: {
        ...mapState('context', ['profile']),
        hasNotes() {
            return !isEmpty(this.notes);
        },
        positionProps() {
            if (this.openLeft && !this.openRight) return { left: true };
            if (this.openRight && !this.openLeft) return { right: true };
            // not sure if offset-x is needed but leaving in to avoid any regressions
            return { auto: true, 'offset-x': true };
        },
    },

    methods: {
        post() {
            this.$emit('post', { text: this.text });
            this.text = null;
        },

        save(note) {
            this.$emit('save', { ...note, text: this.editText });
            this.clear();
        },

        clear() {
            this.noteToEdit = null;
            this.editText = null;
            this.noteToDelete = null;
            this.showConfirmDelete = false;
        },

        deleteNote(noteId) {
            this.showConfirmDelete = true;
            this.noteToDelete = noteId;
        },

        confirmDelete() {
            this.$emit('delete', this.noteToDelete);
            this.clear();
        },

        editNote(note) {
            this.noteToEdit = note._id;
            this.editText = note.text;
        },

        close() {
            this.showNotes = false;
        },

        noteActionsDisabled(note) {
            // User cannot delete a note if they did not create it
            // Should disable actions for a note when editing it.
            return (
                this.profile &&
                (this.profile._id !== note.createdBy || note._id === this.noteToEdit)
            );
        },

        isSaveButtonDisabled(text) {
            return !text || text.length === 0;
        },

        getInitials(note) {
            if (note && note.user) {
                const { firstname, surname } = note.user;
                return this.toSentenceCase(`${firstname[0]}${surname[0]}`);
            }
            return '';
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

:deep(.notes-wrapper) {
    z-index: $notes-z-index !important;
    border-radius: 0;
    box-shadow: 0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14),
        0px 3px 14px 2px rgba(0, 0, 0, 0.12);
}

.notes-icon {
    background: none !important;

    &.v-btn--icon:not(.notes-icon--active) {
        font-size: 1.9rem !important;

        :deep(svg) {
            height: 20px !important;
            width: 20px !important;
        }
    }

    &--active {
        :deep(svg) {
            height: 18px !important;
            width: 18px !important;
        }
    }
}

.notes {
    width: 32rem;
    max-height: 85vh;
    overflow-y: auto;
    background: $promo-white;
    padding: 0.7rem;
    border-left: 0.5rem solid $promo-primary-colour;

    &__header {
        font-size: 1.4rem;
        padding: 0.3rem 0 1rem 1rem;
        display: flex;
        justify-content: space-between;

        h3 {
            font-size: 1.4rem;
            font-weight: 600;
        }
    }

    &__container {
        display: flex;
        background: $promo-grey-5;
        padding: 1rem 0.7rem;
        margin-bottom: 0.5rem;
    }

    &__author {
        height: 2.6rem;
        min-width: 2.6rem;
        width: 2.6rem;

        .headline {
            font-size: 1.2rem !important;
            font-weight: 500;
        }
    }

    &__content {
        width: calc(100% - 3.6rem);
        padding-left: 1rem;
    }

    &__date {
        font-weight: bold;
        font-size: 1.2rem;
        margin-bottom: 0;
    }

    &__date-container {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    &__input-container {
        padding: 1rem 1rem 0.5rem;
        background: $promo-grey-6;
    }

    &__button {
        position: relative;
        margin-top: 0.5rem;
        display: flex;
        justify-content: flex-start;

        :deep(.v-btn) {
            height: 1.8rem !important;
            min-width: auto;
            padding: 0 0.8rem !important;
            font-size: 1.1rem;
        }
    }

    &__text {
        padding-right: 0.5rem;
        font-size: 1.2rem;
        word-break: break-word;

        p {
            margin-bottom: 0;
            font-size: 1.2rem;
        }
    }

    &__input {
        :deep(.v-textarea) {
            border-radius: 0;
            box-shadow: none;

            .v-field {
                border-radius: 0;
                box-shadow: none;
                background: $promo-white;

                &--variant-solo,
                &--variant-filled,
                &--variant-solo-filled {
                    background: $promo-white;
                }
            }

            .v-field__outline {
                &::before,
                &::after {
                    border: none !important;
                }
            }

            .v-field--variant-filled .v-field__outline::after,
            .v-field--variant-underlined .v-field__outline::after {
                border: none !important;
            }
        }

        :deep(.v-input) {
            font-size: 1.2rem !important;

            --v-input-control-height: auto;
            --v-field-padding-bottom: 0;
            --v-field-input-padding-top: 0;
            --v-field-input-padding-bottom: 0;
            --v-input-padding-top: 0;
        }

        :deep(textarea.v-field__input) {
            min-height: 2.4rem;
            padding: 0.5rem;
            font-size: 1.2rem;
            margin: 0;
            font-size: 1.2rem;
            line-height: 1.4;

            &::placeholder {
                color: rgba(0, 0, 0, 0.54);
                opacity: 1;
            }
        }

        :deep(.v-label) {
            font-size: 1.2rem;
            top: 0.5rem !important;
        }

        :deep(.v-input__control) {
            .v-field {
                border-radius: 0;
                box-shadow: none;
                padding-inline-start: 0;
                padding-inline-end: 0;
            }
        }

        :deep(.v-field__overlay) {
            background: $promo-white !important;
            opacity: 1 !important;
        }
    }
}
</style>
